import { authQueryString } from './auth-config';
const DEFAULT_RECONNECT = {
    maxAttempts: 10,
    delayMs: 3000,
    pingIntervalMs: 30000,
    noReconnectCloseCodes: [4401],
};
/**
 * WebSocket auto-réparant : keepalive (ping applicatif) + reconnexion bornée
 * sur drop inattendu, avec le token d'auth re-résolu à CHAQUE (re)connexion.
 *
 * Cycle de vie :
 *  - drop transitoire        → reconnexion silencieuse (`onReconnecting`), la
 *                              promesse appelante reste en attente ;
 *  - tentatives épuisées /   → `onError` (l'échec est rendu visible — l'appelant
 *    reconnexion désactivée    le transforme en rejet de promesse) ;
 *  - `close()` ou `markDone()` → `onClose` (fin propre, aucune reconnexion).
 */
export class ManagedWebSocket {
    constructor(urlFactory, handlers, reconnect) {
        this.urlFactory = urlFactory;
        this.handlers = handlers;
        this.ws = null;
        this.reconnectTimer = null;
        this.pingTimer = null;
        this.attempts = 0;
        this.closedByClient = false;
        this.terminal = false;
        this.cfg = { ...DEFAULT_RECONNECT, ...(reconnect ?? {}) };
        this._open();
    }
    /**
     * Marque l'échange comme terminé (un événement terminal a été reçu) : la
     * prochaine fermeture est attendue et ne doit PAS déclencher de reconnexion.
     */
    markDone() {
        this.terminal = true;
    }
    /** Fermeture délibérée : stoppe ping + reconnexion et ferme la socket. */
    close(code, reason) {
        this.closedByClient = true;
        this._clearReconnect();
        this._stopPing();
        try {
            this.ws?.close(code, reason);
        }
        catch {
            /* déjà fermée */
        }
    }
    /** Envoie une frame brute si la socket est ouverte. */
    send(data) {
        if (this.ws?.readyState === WebSocket.OPEN)
            this.ws.send(data);
    }
    get readyState() {
        return this.ws?.readyState ?? WebSocket.CLOSED;
    }
    _open() {
        let ws;
        try {
            ws = new WebSocket(this.urlFactory());
        }
        catch {
            // Le constructeur peut lever sur une URL invalide — on traite comme un
            // essai échoué.
            this._scheduleReconnectOrFail(null);
            return;
        }
        this.ws = ws;
        ws.onopen = () => {
            this.attempts = 0;
            this._startPing();
        };
        ws.onmessage = (event) => {
            try {
                this.handlers.onMessage(JSON.parse(event.data));
            }
            catch (err) {
                console.error('Error parsing websocket message:', err);
            }
        };
        // Les erreurs par-tentative sont avalées : le navigateur émet toujours un
        // `close` ensuite, c'est là qu'on décide reconnexion vs échec définitif.
        ws.onerror = () => { };
        ws.onclose = (event) => {
            this._stopPing();
            if (this.closedByClient || this.terminal) {
                this.handlers.onClose?.(event);
                return;
            }
            if (this.cfg.noReconnectCloseCodes.includes(event.code)) {
                // Échec permanent (ex. auth 4401) : retenter ne sert à rien, on remonte
                // l'erreur tout de suite pour que l'appelant réagisse (rafraîchir le token).
                this.handlers.onError?.(event);
                this.handlers.onClose?.(event);
                return;
            }
            this._scheduleReconnectOrFail(event);
        };
    }
    _scheduleReconnectOrFail(event) {
        if (this.cfg.maxAttempts > 0 && this.attempts < this.cfg.maxAttempts) {
            this.attempts += 1;
            this.handlers.onReconnecting?.(this.attempts, this.cfg.maxAttempts);
            this.reconnectTimer = setTimeout(() => this._open(), this.cfg.delayMs);
            return;
        }
        // Plus de tentatives (ou reconnexion désactivée) : on rend l'échec visible.
        this.handlers.onError?.(event ?? new Event('error'));
        if (event)
            this.handlers.onClose?.(event);
    }
    _startPing() {
        this._stopPing();
        if (this.cfg.pingIntervalMs <= 0)
            return;
        this.pingTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN)
                this.ws.send('ping');
        }, this.cfg.pingIntervalMs);
    }
    _stopPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }
    _clearReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
export class WebsocketService {
    constructor(options) {
        this.baseUrl = options.baseUrl;
        this.tokenProvider =
            typeof options.token === 'function'
                ? options.token
                : () => options.token;
        this.reconnect = options.reconnect;
    }
    subscribeToChannel(path, onMessage, onClose = () => { }, onError = () => { }, onReconnecting) {
        // Le token est résolu au moment de CHAQUE (re)connexion (pas à l'init du
        // service) pour toujours utiliser la valeur la plus fraîche disponible.
        const urlFactory = () => `${this.baseUrl}/${path}?${authQueryString(this.tokenProvider())}`;
        // Construit les handlers sans clé `onReconnecting: undefined` explicite
        // (incompatible avec `exactOptionalPropertyTypes`).
        const handlers = { onMessage, onClose, onError };
        if (onReconnecting)
            handlers.onReconnecting = onReconnecting;
        return new ManagedWebSocket(urlFactory, handlers, this.reconnect);
    }
    subscribeToExecution(workflowExecution, onExecutionUpdate, onWorkflowExecutionEnd, closeOnKey = null, onCloseKey = () => { }, onClose = () => { }, onError = () => { }, onReconnecting) {
        const onMessage = (data) => {
            if (data.type === 'node_execution_update') {
                const nodeExecution = data.node_execution;
                onExecutionUpdate(nodeExecution);
                if (closeOnKey &&
                    nodeExecution.key === closeOnKey &&
                    nodeExecution.status === 'completed') {
                    onCloseKey(nodeExecution);
                    ws.close(); // fermeture délibérée → pas de reconnexion
                }
            }
            else if (data.type === 'workflow_execution_complete') {
                // Terminal : le serveur ferme la socket juste après. On marque "done"
                // pour que cette fermeture ne soit pas vue comme un drop (sinon tempête
                // de reconnexions après chaque exécution réussie).
                ws.markDone();
                onWorkflowExecutionEnd(data.workflow_execution);
            }
        };
        const ws = this.subscribeToChannel(`execution/${workflowExecution.workflow_execution_id}`, onMessage, onClose, onError, onReconnecting);
        return ws;
    }
    subscribeToNotifications(onNotification, onClose = () => { }, onError = () => { }, onReconnecting) {
        const ws_org = this.subscribeToChannel(`notification`, onNotification, onClose, onError, onReconnecting);
        const ws_user = this.subscribeToChannel(`notification/user`, onNotification, onClose, onError, onReconnecting);
        return [ws_org, ws_user];
    }
}
//# sourceMappingURL=websocket.service.js.map